import React, { useState } from 'react';
import { VideoOverlayTrack } from './types';
import { useVideoTrackPreview } from './useVideoTrackPreview';
import TooltipV2 from '@/components/TooltipV2';
import { classnames } from '@/libs/utils';
import Icon from '@/components/atoms/Icon';

interface VideoTrackCardProps {
  track: VideoOverlayTrack;
  isSelected: boolean;
  onClick: ((videoUrl: string) => void) | undefined;
  disabled?: boolean;
  showTooltip?: boolean;
}

/**
 * Formats duration in seconds to MM:SS format
 */
const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const VideoTrackCard: React.FC<VideoTrackCardProps> = ({
  track,
  isSelected,
  onClick,
  disabled = false,
  showTooltip = true
}) => {
  const formattedDuration = track.duration ? formatDuration(track.duration) : null;
  const [isPlaying, setIsPlaying] = useState(false);

  const { videoContainerRef, progress, videoDuration, hasAudio, isLoaded, isBuffering } = useVideoTrackPreview({
    videoUrl: track.videoUrl,
    isPlaying,
    onPlayingChange: setIsPlaying
  });

  const showThumbnail = !isLoaded;

  const handleClick = () => {
    if (disabled) {
      return;
    }

    if (onClick) {
      onClick(track.videoUrl);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handleMouseEnter = () => {
    if (disabled) {
      return;
    }
    setIsPlaying(true);
  };

  const handleMouseLeave = () => {
    if (disabled) {
      return;
    }
    setIsPlaying(false);
  };

  const hasTags = track.tags && track.tags.length > 0;
  const hasDuration = formattedDuration !== null;

  const cardContent = (
    <div
      role={onClick ? 'button' : 'presentation'}
      tabIndex={disabled ? -1 : 0}
      data-testid="video-track-card"
      onClick={onClick ? handleClick : undefined}
      onKeyDown={onClick ? handleKeyDown : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={classnames({
        'group w-full select-none overflow-hidden rounded-lg border-2 text-left outline-none ring-0 transition-all':
          true,
        'opacity-50': disabled,
        'cursor-pointer': !disabled && onClick,
        'hover:border-deep-orange-200 hover:bg-deep-orange-50 focus:outline-none focus:ring-0': !disabled && onClick,
        'border-deep-orange-500 bg-deep-orange-50 hover:border-deep-orange-500': isSelected,
        'border-slate-200 bg-white': !isSelected
      })}
      aria-label={`Select video: ${track.name}`}
      aria-disabled={disabled}
    >
      {/* Video preview - shows thumbnail while loading, plays on hover */}
      <div
        ref={videoContainerRef}
        data-testid="video-track-thumbnail-container"
        className="relative aspect-video w-full bg-slate-100"
      >
        {/* Thumbnail - shown while video is loading or buffering */}
        {showThumbnail && track.thumbnailUrl && (
          <img
            src={track.thumbnailUrl}
            alt={track.name || 'Video thumbnail'}
            className="absolute inset-0 size-full object-cover"
            data-testid="video-track-thumbnail"
          />
        )}

        {/* Buffering indicator - shown when video is buffering during playback */}
        {isPlaying && isBuffering && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-black/30"
            data-testid="video-track-buffering-indicator"
          >
            <Icon name="IconLoader2" size={24} className="animate-spin text-white" aria-label="Video buffering" />
          </div>
        )}

        {/* Audio indicator - shown when video is playing */}
        {isPlaying && (
          <div
            className="absolute bottom-2 right-2 z-10 flex items-center justify-center rounded-full bg-black/60 p-1.5"
            data-testid="video-track-audio-indicator"
            aria-label={hasAudio ? 'Video has audio' : 'Video has no audio'}
          >
            {hasAudio ? (
              <Icon name="IconVolume" size={16} className="text-white" />
            ) : (
              <Icon name="IconVolumeOff" size={16} className="text-white" />
            )}
          </div>
        )}
      </div>

      {/* Track Info */}
      <div className="relative p-3">
        {/* Progress bar - shown when video is playing, positioned over metadata area */}
        {isPlaying && videoDuration > 0 && (
          <div
            className="absolute inset-x-0 top-0 h-0.5 bg-transparent"
            data-testid="video-track-progress-bar-container"
            style={{ zIndex: 2 }}
          >
            <div
              className="h-full bg-deep-orange-500"
              data-testid="video-track-progress-bar"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {/* Track Name */}
        <div data-testid="video-track-name" className="truncate text-sm font-medium leading-5 text-slate-800">
          {track.name || 'Untitled Video'}
        </div>

        {/* Metadata: Duration */}
        {hasDuration && (
          <div data-testid="video-track-metadata" className="mt-0.5 text-xs text-gray-400">
            <span>{formattedDuration}</span>
          </div>
        )}

        {/* Tags/Description */}
        {hasTags && (
          <div data-testid="video-track-description" className="mt-1 line-clamp-2 text-xs text-gray-400">
            {track.tags.join(', ')}
          </div>
        )}
      </div>
    </div>
  );

  if (!showTooltip) {
    return cardContent;
  }

  return (
    <TooltipV2
      tooltipId={track.id}
      tooltipElement={cardContent}
      tooltipContent={track.name || 'Untitled Video'}
      size="xsmall"
      place="top"
    />
  );
};

export default VideoTrackCard;
